import React, { useState } from 'react';

import ColoredDot from '~/components/core/Atomic/ColoredDot';
import EditableChip from '~/components/core/Molecules/EditableChip';
import LoadingIndicator from '~/components/LoadingIndicator';
import useDataFetcher from '~/components/useDataFetcher';
import { clearUserCache } from '~/Utils';

interface UserOrganization {
  id: number;
  name: string;
  is_current_login: boolean;
  sso_enabled: boolean;
  switch_available: boolean;
  switch_unavailable_reason: string | null;
}

const ORG_COLORS = [
  'teal-600',
  'blue-600',
  'purple-600',
  'orange-700',
  'berry-600',
  'blueSky-700',
  'pink-600',
  'berry-700',
  'purple-700',
  'grey-600',
] as const;

type OrgColor = (typeof ORG_COLORS)[number];

function getOrgColor(orgId: number): OrgColor {
  // Use org ID (not array index) so each org keeps a stable color regardless of list position
  return ORG_COLORS[orgId % ORG_COLORS.length];
}

const OrgOptionDescription: React.FC<{ name: string; color: OrgColor }> = ({ name, color }) => (
  <div className="flex items-center gap-8">
    <ColoredDot color={color} size="XS" />
    {name}
  </div>
);

const OrganizationSwitcher: React.FC = () => {
  const [isSwitching, setIsSwitching] = useState(false);
  const { isLoading, isError, data } = useDataFetcher<UserOrganization[]>('/api/login/user_organizations');

  const currentOrgId = data?.find((org) => org.is_current_login)?.id;

  const options = data?.map((org) => {
    const color = getOrgColor(org.id);
    return {
      id: String(org.id),
      description: <OrgOptionDescription name={org.name} color={color} />,
      color: 'slate-800' as const,
      disabled: !org.switch_available && !org.is_current_login,
    };
  });

  if (isLoading || isError || !data || data.length <= 1) {
    return null;
  }

  const handleChange = async (orgId: string) => {
    setIsSwitching(true);
    await clearUserCache();
    window.location.href = `/api/login/${orgId}`;
  };

  return (
    <>
      <EditableChip
        disabled={isSwitching}
        value={currentOrgId !== undefined ? String(currentOrgId) : null}
        options={options ?? []}
        onChange={handleChange}
      />
      {isSwitching && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/60">
          <LoadingIndicator />
        </div>
      )}
    </>
  );
};

export default OrganizationSwitcher;
